import React, { useState, useEffect, useRef } from "react";
import uuid from "uuid/v4";
import MGridForm from "../MGridForm";
import { MGridFormDefinition } from "components/Shared/MGridForm/MGridFormProps";
import { MCard } from "components/Shared";
import Button from "@mui/material/Button";

export interface MGridRepeatFormControlMethods {
  validate: () => boolean;
  getData: () => any[];
};

interface Props {
  /**Form Definition */
  formDefinition: MGridFormDefinition[][];
  /**Array of data objects */
  initialData: object[];
  /**Callback for getting control methods after renders */
  getControlMethods: (controlMethods: MGridRepeatFormControlMethods) => void;
  /**Title to display above every repeat instance */
  title?: string;
  /**Minimum number of repeat instances. default 0 */
  minInstances?: number;
  /**Maximum number of repeat instances. default infinity */
  maxInstances?: number;
  /**Text to display for add button. default '+ Add' */
  addButtonText?: string;
  /**Text to display for remove button. default '- Remove' */
  removeButtonText?: string;
};

const MGridRepeatForm = ({
  formDefinition,
  initialData,
  getControlMethods,
  title,
  minInstances,
  maxInstances,
  addButtonText,
  removeButtonText,
} : Props) => {

  const [data, setData] = useState<any[]>(structuredClone(initialData));  
  const formRefs = useRef([]);

  const _addButtonText = addButtonText ?? "+ Add";
  const _removeButtonText = removeButtonText ?? "- Remove";

  const validate = () => {
    return formRefs.current.reduce((acc, val) => {
      const valid = val.isFormValid();
      return acc && valid;
    }, true);
  };
  const getData = () => {
    return structuredClone(data);
  };

  useEffect(() => {
    while (formRefs.current.length > data.length)
      formRefs.current.pop();

    if (getControlMethods)
      getControlMethods({
        validate: validate,
        getData: getData,
      });
  }); // no dependencies... this needs to run on every render

  const addInstance = () => {
    const next = structuredClone(data);
    next.push({});
    setData(next);
  };
  const removeInstance = (index) => {
    const next = structuredClone(data);
    next.splice(index, 1);
    setData(next);
  };
  const updateInstance = (index, updatedData) => {
    data[index] = structuredClone(updatedData);
  };

  return (
  <div className="mgrid-repeat-form">
    {data && !data.length &&
    <h3>{`${title ?? "Item"}`}</h3>
    }
    {data && data.map((d, i) =>
    (
      <MCard
        key={`${uuid()}`}
        title={`${title ?? "Item"} ${i + 1}`}
      >
        <MGridForm          
          formDefinition={formDefinition}
          data={d}
          onChange={(updatedObject) => { updateInstance(i, updatedObject); }}
          getControlMethods={cm => { formRefs.current[i] = cm; }}
          webform
        />
        {minInstances && data.length > minInstances &&
        <Button
          className="mgrid-repeat-button" color="secondary"
          onClick={() => removeInstance(i)}
        >
        {_removeButtonText}
        </Button>
        }
      </MCard>
    ))
    }
    {(!maxInstances || (maxInstances && data.length < maxInstances)) &&
    <Button
      className="mgrid-repeat-button"
      onClick={addInstance}
    >
    {_addButtonText}
    </Button>
    }
  </div>
  );
};

export default MGridRepeatForm;